import { rem } from 'polished';
import { Color } from './helpers';

interface IColorStringList {
  [key: string]: string;
}

// tslint:disable: object-literal-sort-keys

/**
 * WCAG 2.1 Colors
 *
 * These colors are part of the cybs-ui WCAG 2.1 upgrade. Names are provided by
 * UX. The inline comments on these track some preliminary usage, but are not
 * necessarily 100% accurate or exhaustive.
 */
export const wcag21Colors: IColorStringList = {
  // greyscale, darkest to lightest. typically used for borders and text
  charcoal: '#000000',
  slateGrey: '#021E4C', // no longer in use
  stoneGrey: '#62798D',
  disabledGrey: '#00000033', // 33 is hex value for 20% transparency: https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
  silverGrey: '#C7EDFF', // no longer in use
  pearlGrey: '#F0F0F0',
  white: '#FFFFFF',

  // blues, darkest to lightest. used for most interactable elements
  midnightBlue: '#021E4C',
  denimBlue: '#0A2292',
  cobaltBlue: '#0F2595',
  trueBlue: '#1434CB',
  skyBlue: '#0A2292', // no longer in use
  frostBlue: '#C7EDFF',

  // reds, dark and light. stuff thats in error
  romaRed: '#B3202A',
  blushRed: '#FFD6E9',

  // oranges, dark and light. warning colors
  amberOrange: '#9B6603',
  peachOrange: '#FFF7CC', // rolled back to Vault yellow for contrast requirements

  // greens, dark and light. positive stuff
  collardGreen: '#2D6C4B',
  mintGreen: '#D6F2C4'
};

/**
 * @deprecated
 * This has been overriden by WCAG 2.1. Check commit on this comment if you
 * need more info.
 *
 * Cyber Source (CYBS) - Rebrand Color Palette
 */
export const cybsColors: IColorStringList = {
  // Primary
  blue: wcag21Colors.trueBlue,
  navy: wcag21Colors.cobaltBlue,

  // Secondary
  darkBlue: wcag21Colors.midnightBlue,

  // Background
  moon: wcag21Colors.pearlGrey,
  lightGray: wcag21Colors.pearlGrey,
  mediumGray: wcag21Colors.stoneGrey,
  gray: wcag21Colors.slateGrey
};

/**
 * @deprecated
 * Visa Design System (VDS) - VAULT Color Palette
 */
export const vaultColors: IColorStringList = {
  // Primary
  darkBlue: '#1a1f71',
  night: '#15195a',

  // Actions
  blue: '#003ea9',
  lightBlue: '#0051dc',

  // Text
  gray: '#767676',
  lead: '#222222',

  // Elements
  indigo: '#2a2e69',
  plum: '#616188',

  // Secondary
  cloudy: '#b4b4c6',
  ice: '#e0eafa',
  smoke: '#d7d7de',

  // Accent
  gold: '#f7b600',

  // Background
  fog: '#f2f4f8',
  milk: '#ecece8',

  // General purpose
  white: '#ffffff',

  // Messaging
  green: '#077844',
  orange: '#ff9800',
  red: '#d50000'
};

// tslint:enable: object-literal-sort-keys

const themeColors = {
  action: {
    link: {
      default: wcag21Colors.trueBlue,
      disabled: wcag21Colors.stoneGrey
    },
    primary: {
      dark: wcag21Colors.cobaltBlue,
      default: wcag21Colors.trueBlue,
      extraDark1: wcag21Colors.denimBlue,
      extraDark2: wcag21Colors.midnightBlue,
      extraLight: wcag21Colors.pearlGrey,
      light: wcag21Colors.skyBlue
    },
    secondary: {
      default: wcag21Colors.cobaltBlue,
      light: wcag21Colors.cobaltBlue
    }
  },

  application: {
    background: wcag21Colors.pearlGrey,
    disabled: wcag21Colors.stoneGrey,
    overlay: new Color('#000').withAlpha(0.15).toString(),
    topbar: wcag21Colors.white
  },

  component: {
    footer: {
      background: wcag21Colors.white,
      link: wcag21Colors.trueBlue,
      text: wcag21Colors.charcoal
    },
    navbar: {
      background: wcag21Colors.pearlGrey,
      backgroundSelected: wcag21Colors.white,
      foreground: wcag21Colors.cobaltBlue,
      indicatorBar: wcag21Colors.trueBlue
    },
    table: {
      alt: wcag21Colors.pearlGrey,
      highlight: wcag21Colors.frostBlue,
      selected: wcag21Colors.stoneGrey
    },
    topbar: {
      background: wcag21Colors.white,
      foreground: wcag21Colors.midnightBlue,
      partnerLink: wcag21Colors.trueBlue,
      portalText: wcag21Colors.charcoal,
    }
  },

  status: {
    error: {
      background: wcag21Colors.blushRed,
      foreground: wcag21Colors.romaRed
    },
    information: {
      background: wcag21Colors.silverGrey,
      foreground: wcag21Colors.slateGrey
    },
    success: {
      background: wcag21Colors.mintGreen,
      foreground: wcag21Colors.collardGreen
    },
    warning: {
      background: wcag21Colors.peachOrange,
      foreground: wcag21Colors.amberOrange
    }
  },

  element: {
    dark: wcag21Colors.charcoal,
    default: wcag21Colors.slateGrey,
    highlight: wcag21Colors.frostBlue,
    light: wcag21Colors.stoneGrey
  },

  misc: {
    dark: wcag21Colors.slateGrey,
    extraDark: wcag21Colors.charcoal,
    extraLight: wcag21Colors.pearlGrey,
    light: wcag21Colors.silverGrey,
    medium: wcag21Colors.stoneGrey,
    transparent: new Color('#000').withAlpha(0).toString(),
    white: wcag21Colors.white
  }
};

// Structure
const themeSpacing = [
  '0rem',
  '0.5rem',
  '1.0rem',
  '1.5rem',
  '2.0rem',
  '2.5rem',
  '3.0rem',
  '3.5rem',
  '4.0rem'
];

// Fonts
const fontFamily = {
  black: 'relative-book, OpenSans Black, Roboto, Helvetica, Arial, sans-serif',
  blackItalic:
    'relative-book, OpenSans Black Italic, Roboto, Helvetica, Arial, sans-serif',
  bold: 'relative-book, OpenSans Bold, Roboto, Helvetica, Arial, sans-serif',
  boldItalic:
    'relative-book, OpenSans Bold Italic, Roboto, Helvetica, Arial, sans-serif',
  light: 'relative-book, OpenSans Light, Roboto, Helvetica, Arial, sans-serif',
  lightItalic:
    'relative-book, OpenSans Light Italic, Roboto, Helvetica, Arial, sans-serif',
  medium:
    'relative-book, OpenSans Medium, Roboto, Helvetica, Arial, sans-serif',
  mediumItalic:
    'relative-book, OpenSans Medium Italic, Roboto, Helvetica, Arial, sans-serif',
  regular: 'relative-book, OpenSans, Roboto, Helvetica, Arial, sans-serif',
  regularItalic:
    'relative-book, OpenSans Regular Italic, Roboto, Helvetica, Arial, sans-serif',
  thin: 'relative-book, OpenSans Thin, Roboto, Helvetica, Arial, sans-serif',
  thinItalic:
    'relative-book, OpenSans Thin Italic, Roboto, Helvetica, Arial, sans-serif'
};

// Weight
const fontWeight = {
  '100': 100,
  '200': 200,
  '300': 300,
  '400': 400,
  '500': 500,
  '600': 600,
  '700': 700,
  '800': 800,
  '900': 900
};

// Changing the base font-size from 10px to 14px as we will be relying on external styles.
const remWBase = (val: number) => rem(val, 16);

// Sizes
const fontSize = {
  base: '16px',
  body: remWBase(14),
  h1: remWBase(22),
  h2: remWBase(20),
  h3: remWBase(18),
  h4: remWBase(16),
  h5: remWBase(14),
  h6: remWBase(14),
  small: remWBase(12),
  tiny: remWBase(10)
};

// Icons
const iconSize = ['1rem', '1.1rem', '1.6rem', '2rem', '2.4rem', '3rem', '4rem'];

const themeTypography = {
  fontFamily,
  fontSize,
  fontWeight,
  iconSize
};

// General Borders
const borders = {
  style: {
    dashed: 'dashed',
    default: 'solid',
    dotted: 'dotted',
    solid: 'solid'
  },
  width: {
    default: '0.1rem',
    wide: '0.2rem'
  }
};

// Browser Breakpoints
const breakpoints = {
  large: '1440px',
  medium: '1024px',
  small: '640px',
  xlarge: '1920px',
  xxlarge: '1921px'
};

// Browser Breakpoints
const zIndex = {
  '0': 0,
  '1': 100,
  '2': 200,
  '3': 300,
  '4': 400,
  '5': 500,
  '6': 600,
  '7': 2000
};

const themeMisc = {
  borders,
  breakpoints,
  zIndex
};

export interface IVariables {
  base: {
    cybs: any;
    vault: any;
    wcag21: any;
  };
  colors: any;
  layout: {
    spacing: any;
  };
  misc: any;
  type: any;
}

export const variables: IVariables = {
  base: {
    cybs: cybsColors,
    vault: vaultColors,
    wcag21: wcag21Colors
  },
  colors: themeColors,
  layout: {
    spacing: themeSpacing
  },
  misc: themeMisc,
  type: themeTypography
};
